import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { rgba } from "polished";

import {
  Box,
  Button,
  Container,
  Grid,
  Tooltip,
  Typography as MuiTypography,
} from "@mui/material";
import { spacing } from "@mui/system";
import { Visibility as VisibilityIcon } from "@mui/icons-material";
import { ArrowForward as ArrowForwardIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

const Typography = styled(MuiTypography)(spacing);

const Wrapper = styled.div`
  padding-top: 3.5rem;
  position: relative;
  text-align: center;
  overflow: hidden;
`;

const Content = styled.div`
  padding: ${(props) => props.theme.spacing(6)} 0;
  line-height: 150%;
`;

const Image = styled.img`
  max-width: 100%;
  height: auto;
  display: block;
  box-shadow: 0 6px 18px 0 rgba(18, 38, 63, 0.075);
  border-radius: 5px;
  transform: perspective(2000px) rotateX(25deg);
  z-index: 0;
  position: relative;
  image-rendering: optimizequality;
  image-rendering: -webkit-optimize-contrast;
  margin-bottom: -100px;
  margin-top: -35px;
  ${(props) => props.theme.breakpoints.up("md")} {
    margin-top: -50px;
  }
`;

const ImageWrapper = styled.div`
  &:before {
    background: linear-gradient(transparent, rgba(0, 0, 0, 0.02));
    bottom: 0;
    left: 0;
    position: absolute;
    content: " ";
    z-index: 1;
    display: block;
    width: 100%;
    height: 75px;
    pointer-events: none;
  }
`;

const Title = styled(Typography)`
  opacity: 0.9;
  line-height: 1.4;
  font-size: 1.75rem;
  font-weight: ${(props) => props.theme.typography.fontWeightBold};

  ${(props) => props.theme.breakpoints.up("sm")} {
    font-size: 2rem;
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 2.5rem;
  }

  span {
    color: ${(props) => props.theme.palette.secondary.main};
  }
`;

const Subtitle = styled(Typography)`
  font-size: ${(props) => props.theme.typography.h6.fontSize};
  font-weight: ${(props) => props.theme.typography.fontWeightRegular};
  font-family: ${(props) => props.theme.typography.fontFamily};
  margin: ${(props) => props.theme.spacing(2)} 0;
`;

const BrandIcon = styled.img`
  vertical-align: middle;
  // margin-right: ${(props) => props.theme.spacing(3)};
  height: 48px;
  width: auto;
`;

const Visibility = styled(VisibilityIcon)`
  margin-right: ${(props) => props.theme.spacing(2)};
`;

const ArrowForward = styled(ArrowForwardIcon)`
  margin-left: ${(props) => props.theme.spacing(2)};
`;

const Version = styled(MuiTypography)`
  color: ${(props) => props.theme.palette.primary.main};
  font-weight: ${(props) => props.theme.typography.fontWeightBold};
  background: ${(props) => rgba(props.theme.palette.primary.main, 0.1)};
  box-shadow: 0 1px 1px
    ${(props) => rgba(props.theme.palette.primary.main, 0.25)};
  padding: 3px 8px;
  border-radius: 4px;
  margin-bottom: ${(props) => props.theme.spacing(3)};
  display: inline-block;
`;

function Introduction() {
  const { t } = useTranslation();

  return (
    <Wrapper>
      <Container>
        <Grid container alignItems="center" justifyContent="center" spacing={4}>
          <Grid size={{ xs: 12, sm: 9, md: 8, lg: 8 }}>
            <Content>
              <Version variant="body2">v4.4.0</Version>
              <Title variant="h1" gutterBottom>
                De slimme manier om al uw <span>contracten</span> onder controle
                te krijgen.
              </Title>
              <Grid container justifyContent="center" spacing={4}>
                <Grid size={{ xs: 12, lg: 10 }}>
                  <Subtitle color="textSecondary">
                    Haal meer waarde uit uw contracten met onze AI Contract
                    Manager. Slimme AI-technologie analyseert, beheert en
                    optimaliseert uw documenten waardoor u altijd de volledige
                    controle en inzicht heeft in de gemaakte afspraken.
                  </Subtitle>
                </Grid>
              </Grid>
              <Box my={6}>
                <Button
                  href="#demos"
                  variant="contained"
                  color="secondary"
                  size="large"
                >
                  <Visibility />
                  {t("Product tour")}
                  <ArrowForward />
                </Button>
              </Box>

              <Typography variant="body2" color="textSecondary">
                Hoge beveiligingsstandaarden en integraties met Microsoft 365.
              </Typography>
              <Box
                my={3}
                display="flex"
                gap={10}
                justifyContent={"center"}
                alignItems="center"
                flexWrap="wrap"
              >
                <Tooltip title="ISO 27001">
                  <BrandIcon
                    alt="ISO 27001"
                    src="https://www.keppeldatacentres.com/file/sustainability/our-green-missions/certifications-and-awards/icons/iso-27001.png"
                    style={{ height: 80 }}
                  />
                </Tooltip>
                <Tooltip title="Microsoft 365">
                  <BrandIcon
                    alt="Microsoft 365"
                    src="https://cdn.brandfetch.io/idEzoI2jXq/w/1857/h/301/theme/dark/logo.png?c=1bxid64Mup7aczewSAYMX&t=1741363634164"
                    style={{ height: 40 }}
                  />
                </Tooltip>
                <Tooltip title="Azure OpenAI">
                  <BrandIcon
                    alt="Figma"
                    src="https://sp-ao.shortpixel.ai/client/to_auto,q_lossy,ret_img,w_869,h_480/https://answerrocket.com/wp-content/uploads/azure-openai-logo-625x345-1.png"
                    style={{ height: 120 }}
                  />
                </Tooltip>
              </Box>
            </Content>
          </Grid>
        </Grid>
        <Grid container alignItems="center" justifyContent="center" spacing={4}>
          <Grid size={{ xs: 12, sm: 10, md: 9, lg: 9 }}>
            <ImageWrapper>
              <NavLink to="/dashboard/analytics" target="_blank">
                <Image
                  alt="Mira - React Admin Template"
                  src={`https://aicontractmanager.nl/wp-content/uploads/2024/05/Screenshot.png`}
                />
              </NavLink>
            </ImageWrapper>
          </Grid>
        </Grid>
      </Container>
    </Wrapper>
  );
}

export default Introduction;
